import * as effects from 'redux-saga/effects';
import { store, runService } from 'store';
import { ExtractByType, IAction } from 'types';

export type ExtractArgs<TFunc> = TFunc extends {(...args: infer TArgs): any}
    ? TArgs
    : never;

type PayloadArgs = {payload: any};


export const delay = ms => new Promise(resolve => setTimeout(() => resolve(true), ms));


export function watch(actionType, effect, arg) {
    return function decorator(target, name, descriptor) {
        target.generatedWatchers.push(function* () {
            yield effects[effect](actionType, descriptor.value);
        });
        descriptor.value.generatedAction = function(payload) {
            this.dispatch({ 'type': actionType, payload });
        };
    };
}

export function latest(target: any, name: string, descriptor: PropertyDescriptor): void {
    const actionType = `@@${target.serviceName}_${name}`;
    target.generatedWatchers.push(function* () {
        yield effects.takeLatest(actionType, descriptor.value);
    });
    descriptor.value.generatedAction = function(payload) {
        this.dispatch({ 'type': actionType, payload });
    };
}

export function every(target: any, name: string, descriptor: PropertyDescriptor): void {
    const actionType = `@@${target.serviceName}_${name}`;
    target.generatedWatchers.push(function* () {
        yield effects.takeEvery(actionType, descriptor.value);
    });
    descriptor.value.generatedAction = function(payload) {
        this.dispatch({ 'type': actionType, payload });
    };
}

export function leading(target: any, name: string, descriptor: PropertyDescriptor): void {
    const actionType = `@@${target.serviceName}_${name}`;
    target.generatedWatchers.push(function* () {
        yield effects.takeLeading(actionType, descriptor.value);
    });
    descriptor.value.generatedAction = function(payload) {
        this.dispatch({ 'type': actionType, payload });
    };
}

export function debounce(ms: number) {
    return function decorator(target: any, name: string, descriptor: PropertyDescriptor): void {
        const actionType = `@@${target.serviceName}_${name}`;
        target.generatedWatchers.push(function* () {
            yield effects.debounce(ms, actionType, descriptor.value);
        });
        descriptor.value.generatedAction = function(payload) {
            this.dispatch({ 'type': actionType, payload });
        };
    };
}

export function throttle(ms: number) {
    return function decorator(target: any, name: string, descriptor: PropertyDescriptor): void {
        const actionType = `@@${target.serviceName}_${name}`;
        target.generatedWatchers.push(function* () {
            yield effects.throttle(ms, actionType, descriptor.value);
        });
        descriptor.value.generatedAction = function(payload) {
            this.dispatch({ 'type': actionType, payload });
        };
    };
}

export function onStart(target: any, name: string, descriptor: PropertyDescriptor): void {
    target.generatedWatchers.unshift(descriptor.value);
}

export function onStop(target, name, descriptor) {
    const actionType = `@@${target.serviceName}_stop`;

    if (target.stopActionnExists) {
        throw new Error(`${target.serviceName} error: there can only be one stopping action`);
    }
    target.generatedWatchers.push(function* () {
        yield effects.takeEvery(actionType, descriptor.value);
    });
    descriptor.value.generatedAction = function() {
        this.dispatch({ 'type': actionType });
    };
    descriptor.value.onStop = actionType;
    target.stopActionnExists = true;
}

export function nativeWatcher(patternOrChannel) {
    return function decorator(target, name, descriptor) {
        target.generatedWatchers.push(descriptor.value);
        if (typeof patternOrChannel === 'string') {
            descriptor.value.generatedAction = function(payload) {
                this.dispatch({ 'type': patternOrChannel, payload });
            };
        }
    };
}

export function bindActionsTyped <T>(
    ServiceClass: T,
    serviceInstance: Service
) {
    type ActionPayloadType<T> = T extends IAction<infer A> ? A : unknown;
    type ActionType<T> = T extends (action: infer A) => any ? A : unknown;
    type TServiceActions = {
        [K in keyof ExtractByType<T, Function>]: (
            ActionType<T[K]> extends IAction<any>
                ? (payload: ActionPayloadType<ActionType<T[K]>>) => void
                : () => void
        )
    };

    const srvActionKeys = Object.keys(ServiceClass).filter(prop => (
        typeof ServiceClass[prop] === 'function' && ServiceClass[prop].generatedAction
    ));

    let actionsMap: { [key: string]: Function } = {};

    for (let key of srvActionKeys) {
        let _key = ServiceClass[key].onStop || key;
        actionsMap[_key] = (payload): void => {
            if (!serviceInstance.isRunning) {
                const sName = serviceInstance.constructor.name;
                throw new Error(`${sName}: Attention, attempt to call the service's Saga before it starts!`);
            }

            if (typeof ServiceClass[key].generatedAction === 'function') {
                ServiceClass[key].generatedAction.call(store, payload);
            }
        };
    }
    return actionsMap as unknown as TServiceActions;
}

export class Service {
    static serviceName: string;
    static generatedWatchers;
    isRunning: boolean = false;
    rootTask: any;
    actions;

    start = (): void => {
        if (this.isRunning) {
            this.terminate();
        }
        runService(this);
        this.isRunning = true;
    };

    terminate = (): void => {
        const service = this.constructor as typeof Service;
        const actionType = `@@${service.serviceName}_stop`;
        if (this.isRunning) {
            if (this.actions[actionType] !== undefined) {
                this.actions[actionType]();
            }
            this.rootTask.cancel();
            this.isRunning = false;
        }
    };
}
